/* Built In Imports */
import { useRouter } from 'next/router';
import { Fragment } from 'react';

/* External Imports */
import { Box } from '@chakra-ui/react';

/* Internal Imports */
// Components
import MMMCard from '@components/Card/MMMCard';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import config from '@config';
import { LangStyle } from 'constants/index';
import Article from './Article';
import PoemAudioVideoComp from './PoemAudioVideo';

// Services
// Styles
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

/**
 * Component for showing details of the articles.
 * Renders Article Comp Component
 *
 * @component
 * @ArticleComp
 * @param articleData = []
 * @param {string} language - Language of the page
 * @param {boolean} isLikeBookmarkVisible - true or false - to show and hide the like and bookmarks
 * const { type } = router.query;
 * type = 'poem' || 'audio' || 'video'
 * @returns {ReactElement} - Article Comp Component
 */

const ArticleComp = ({
  articleData,
  language,
  isLikeBookmarkVisible,
  updateCards,
  pageType,
}) => {
  const router = useRouter();
  const { type } = router.query;
  return (
    <Fragment>
      {type === 'poem' || type === 'audio' || type === 'video' ? (
        <PoemAudioVideoComp
          type={type}
          articleData={articleData}
          isLikeBookmarkVisible={isLikeBookmarkVisible}
          updateCards={updateCards}
          language={language}
        />
      ) : (
        <Article
          type={type}
          articleData={articleData}
          isLikeBookmarkVisible={isLikeBookmarkVisible}
          updateCards={updateCards}
          language={language}
          pageType={pageType}
        />
      )}

      {articleData?.relatedStories &&
        articleData?.relatedStories?.length > 0 && (
          <Box
            as="section"
            float="left"
            width="100%"
            background={{
              base: 'linear-gradient(to bottom, #ffffff 1%, #ccc3b0 97%)',
              lg: `url('${config.staticPath}/assets/images/ornaments-leaves.svg'), linear-gradient(to bottom, #ffffff 1%, #ccc3b0 97%)`,
            }}
            backgroundRepeat={{ base: 'no-repeat', lg: 'no-repeat, no-repeat' }}
            backgroundSize={{ base: 'cover', lg: '500px, cover' }}
            backgroundPosition={{ base: 'center', lg: '0 80px, center' }}
            mt={{ md: '0', sm: '0' }}
            // backgroundRepeat="no-repeat"
            // backgroundSize={{ lg: '500px, cover', md: 'cover' }}
            // backgroundPosition={{ lg: '0 80px, center', md: 'center' }}
            // mt={{ md: '30px', sm: '0' }}
          >
            <Box
              float="left"
              width="100%"
              mt={{ base: '62px', md: '18px', lg: '18px' }}
            >
              <Box
                float="left"
                width="100%"
                fontFamily="'FedraSansStd-A-medium', 'sans-serif'"
                fontSize={{ base: '24px', md: '30px', lg: '30px' }}
                // bg='https://static.sadhguru.org/assets/images/ornaments-leaves.svg'
                lineHeight="1.13"
                textAlign="center"
                color="#02026c"
              >
                Related Stories
              </Box>
              <Box
                float="left"
                width="250px"
                m="5px calc((100% - 250px) / 2) 0"
              >
                <LazyLoadImageComponent
                  alt="divider"
                  title="Divider"
                  src={`${config.staticPath}/assets/images/divider2.svg`}
                  float="left"
                  width="100%"
                  className="articleDividerImage"
                />
              </Box>
            </Box>
            <Box
              float="left"
              width="100%"
              mt="50px"
              p={{ base: '0 15px 13px', md: '0 80px 80px', lg: '0 80px 80px' }}
            >
              {articleData?.relatedStories &&
                articleData?.relatedStories?.length > 0 && (
                  <MMMCard
                    mmmCardData={articleData?.relatedStories}
                    lang={language}
                  />
                )}
            </Box>
          </Box>
        )}
      <style global jsx>
        {`
          .articlebody-arabic {
            direction: rtl;
          }
          .text-dir {
            direction: rtl;
            unicode-bidi: bidi-override;
          }
          strong {
            font-family: 'FedraSansStd-medium' !important;
            font-weight: normal !important;
          }
          hr {
            border-color: #eee;
          }
          table p {
            margin: 0 0 25px 0 !important;
          }
          td p {
            margin: 0 0 16px 0 !important;
          }
          .articlebody ol,
          .articlebody ul {
            padding-left: 1.5em;
          }
          .articlebody ul li,
          .articlebody ol li {
            padding: 3px 0.6vw;
          }
          .articlebody p {
            margin: 25px 0;
            word-break: break-word;
            direction: ${LangStyle[language]?.direction
              ? LangStyle[language].direction
              : 'none'};
            unicode-bidi: ${LangStyle[language]?.unicodeBidi
              ? LangStyle[language].unicodeBidi
              : 'none'};
          }
          .articlebody p iframe {
            width: 100%;
          }
          .articlebody blockquote {
            padding: 10px 20px;
            margin: 0 0 20px;
            font-size: 17.5px;
            border-left: 5px solid #eee;
          }
          .articlebody h1,
          .articlebody h2,
          .articlebody h3,
          .articlebody h4,
          .articlebody h5 {
            font-family: 'FedraSansStd-A-medium';
            font-size: 36px;
            line-height: 1.11;
            text-align: ${LangStyle[language]?.alignRTL
              ? LangStyle[language].alignRTL
              : 'left !important'};
            color: #28231e !important;
            direction: ${LangStyle[language]?.direction
              ? LangStyle[language].direction
              : 'none'};
            unicode-bidi: ${LangStyle[language]?.unicodeBidi
              ? LangStyle[language].unicodeBidi
              : 'none'};
            margin-top: 20px;
            margin-bottom: 10px;
            word-break: break-word;
          }

          .articlebody h2 {
            font-size: 30px;
            line-height: 1.1;
            // Need to look into this, commenting as of now
            // float: left;
            // width: 100%;
          }

          .articlebody h3 {
            font-size: 27px !important;
          }

          .articlebody h4 {
            font-size: 24px !important;
            line-height: 1.21;
          }

          .articlebody h5 {
            font-size: 20px !important;
            line-height: 1.3;
          }

          .articlebody iframe {
            width: 100%;
            margin: 40px auto;
            // max-height: 405px;
            // min-height: 420px;
          }
          .articlebody a {
            color: #cf4520;
            border-bottom: 1px solid #cf4520;
            padding-bottom: 2px;
            cursor: pointer;
            word-break: break-word;
          }
          .articlebody a:hover {
            background: #cf4520;
            border-bottom: none;
            color: #ffffff !important;
          }
          .articlebody a img {
            padding-bottom: 3px;
            position: relative;
            top: 17px;
          }
          .articlebody .isha-article-quote-bg {
            background-image: url('${config.imgPath}/d/46272/1663649433-iaq.png');
            background-position: 80% 100%;
            background-repeat: no-repeat;
            padding: 4vw 5vw 4vw;
            // margin: 2vw 3vw;
            text-align: right;
          }
          .article-right-blue {
            height: auto;
            right: 0;
            width: auto;
            background-image: url('${config.imgPath}/d/46272/1663649491-article-side-img.png');
            background-repeat: repeat-y;
            background-position-x: right;
          }

          .isha-article-quote {
            /* width: 468px; */
            width: 84%;
            height: auto;
            font-family: ${LangStyle[language]?.articleFontFamily
              ? LangStyle[language].articleFontFamily
              : 'FedraSerifAStdBook'};
            font-size: ${LangStyle[language]?.pullQuoteFontSize
              ? LangStyle[language].pullQuoteFontSize
              : '32px'};
            line-height: 1.28;
            text-align: right;
            color: ${LangStyle[language]?.pullQuoteColor
              ? LangStyle[language].pullQuoteColor
              : '#6f6358'};
            margin-left: 40px;
          }

          .poemBlock {
            float: left;
            width: 100%;
            text-align: center;
            margin: 30px 0;
            padding: 50px 10px;
            padding-left: calc((100% - 750px) / 2);
            padding-right: calc((100% - 750px) / 2);
            // background-image: linear-gradient(to bottom, #f8f7ef 6%, #ffffff 99%);
            font-family: 'FedraSansStd-book-i';
            font-size: 24px;
            line-height: 1.17;
          }
          .poem-poemBlock {
            float: left;
            width: 100%;
            text-align: center;
            margin: 0 0;
            padding: 0 10px;
            padding-left: calc((100% - 750px) / 2);
            padding-right: calc((100% - 750px) / 2);
            background-color: #00334a;
          }

          .poemBlock .heading {
            float: left;
            width: 100%;
            font-family: 'FedraSansStd-medium', sans-serif;
            font-size: 48px;
            text-transform: uppercase;
          }
          .poem-poemBlock .heading {
            color: #faf7f0 !important;
            font-family: 'FedraSerifAStdBook' !important;
            margin: 4vw 0 0.4vw 0 !important;
            text-align: center !important;
            width: 100%;
            font-size: 45px;
            line-height: 1.38;
            letter-spacing: 1px;
            background-position: center bottom !important;
            min-height: auto !important;
            padding-bottom: 49px !important;
            background-image: url(${config.imgPath}/d/46272/1663645833-flower-divider-horizontal-new.svg);
            background-repeat: no-repeat;
          }

          .poemBlock .dividerimg {
            float: left;
            width: 100%;
            margin: 30px 0;
          }
          .poemBlock .dividerimg img {
            float: unset;
            width: auto;
          }
          .poem-poemBlock .poem {
            font-family: 'FedraSansStd-book-i', sans-serif;
            font-size: 24px;
            color: #dfd6c4;
            clear: both;
            padding-top: 5px;
          }
          .poem-poemBlock .poem p {
            margin: 20px 0;
            line-height: 1.17;
            letter-spacing: 1px;
          }
          .isha-article-caption,
          .isha-article-downloadmsg {
            color: #8a8173;
            height: auto !important;
            font-family: 'FedraSansStd-book-i';
            font-size: 20px;
            line-height: 1.7;
            text-align: center;
            margin-bottom: 32px;
          }
          .isha-article-caption {
            display: none;
          }
          .isha-article-downloadmsg .desktop {
            display: block;
          }
          .isha-article-downloadmsg .mobile {
            display: none;
          }
          .image-shortcode {
            text-align: center;
          }
          .image-shortcode.left {
            display: block;
            height: auto;
            float: left;
            width: 100%;
          }
          .image-shortcode .isha-article-caption {
            margin: 0 auto !important;
            height: auto !important;
            margin-bottom: 40px !important;
          }
          .image-shortcode.left .isha-article-caption {
            float: left;
          }
          .divider {
            width: 100%;
            height: 2px;
            border: solid 1px #d33100;
            margin: 20px 0;
          }
          .dropcap {
            background: url('${config.imgPath}/d/46272/1663673807-invalid-name.png')
              no-repeat;
            width: 90px;
            height: 100px;
            text-align: center;
            float: left;
            margin: 10px 10px 10px -20px;
          }
          .dropcap .text {
            margin-top: -10px !important;
            font-size: 84px;
            font-family: 'FedraSerifAStdBook';
            margin-top: -18px;
            position: absolute;
            width: 100px;
            margin-left: -48px;
          }
          .sadhguru-signature {
            min-height: 150px;
            font-family: 'FedraSansStd-book';
            font-size: 24px;
            text-align: left;
            color: #7d7c7a;
            background: url('${config.imgPath}/d/46272/1663654954-signature_name.png')
              no-repeat;
            background-position: 0% 100%;
            margin-bottom: 80px;
            padding-top: 10px;
            letter-spacing: 1px;
            padding-bottom: 115px;
            clear: both;
          }
          .sadhguru-signature.white {
            background: url('${config.imgPath}/d/46272/1663673930-sadhguru_signature_white.svg')
              no-repeat 0% 100%;
            color: #fff;
          }
          .sadhguru-signature.white.center {
            background: url('${config.imgPath}/d/46272/1663673930-sadhguru_signature_white.svg')
              no-repeat center bottom;
            text-align: center;
          }
          .isha-ps {
            font-family: 'FedraSansStd-book-i';
            font-size: 18px;
            line-height: 1.61;
            color: #28231e;
          }
          .quote {
            font-family: 'FedraSansStd-book';
            font-size: 20px;
            line-height: 1.7;
            text-align: left;
            color: #28231e;
            margin-bottom: 2vw;
          }
          .toggle-box-head {
            font-family: 'FedraSansStd-A-medium' !important;
            font-size: 30px !important;
            line-height: 1.1;
            color: #28231e;
            margin-top: 20px;
            margin-bottom: 10px;
            font-weight: 300 !important;
          }
          .isha-audio-player {
            margin-top: 21px;
          }
          .audioplayer {
            display: flex;
            height: 80px;
            opacity: 0.84;
            background-color: #d8d8d8;
            padding: 0;
          }
          .audio {
            width: 720px;
            height: 80px;
            opacity: 0.9;
            color: #d8d8d8;
          }
          .timeline {
            width: 75%;
            height: 2px;
            margin-top: 38px;
            float: left;
            background: #979797;
            padding: 0;
          }
          .mar-top30 {
            margin-top: 30px;
          }
          .tcenter {
            text-align: center;
          }
          .pButton {
            height: 80px;
            width: 60px;
            border: none;
            background-repeat: no-repeat;
            background-position: center;
            outline: none;
            font-family: 'FedraSansStd-medium';
            margin: 16px 0 0 17px;
            background: url('${config.imgPath}/d/46272/1663639558-play.svg')
              no-repeat;
          }
          .playhead {
            width: 17px;
            height: 17px;
            border-radius: 50%;
            margin-top: -9px;
            background: #cf4520;
          }
          .isha-image-credit {
            background: url('${config.imgPath}/d/46272/1663674009-camera.svg')
              no-repeat;
            background-position: left;
            padding-left: 28px;
            line-height: 25px;
          }
          .isha-image-credit a {
            font-family: 'FedraSansStd-book-i';
            font-size: 16px;
          }
          .introtext {
            font-family: 'FedraSansStd-book-i';
            font-size: 20px !important;
            line-height: 1.7;
            color: #28231e;
            margin-top: -10px;
          }
          .btn-isha-1 {
            font-family: 'FedraSansStd-medium';
            color: #ffffff;
            background-color: #cf4520;
            padding: 7px 12px;
            margin-bottom: 0;
            font-size: 14px;
            text-align: center;
            white-space: nowrap;
            vertical-align: middle;
            touch-action: manipulation;
            cursor: pointer;
            user-select: none;
            background-image: none;
            border: 1px solid transparent;
            border-radius: 3px;
          }
          .btn-isha-1:hover {
            background-color: #000054;
            color: #fff;
            -webkit-transition: all 500ms ease;
            -moz-transition: all 500ms ease;
            -ms-transition: all 500ms ease;
            -o-transition: all 500ms ease;
            transition: all 500ms ease;
          }
          .isha-question {
            font-family: 'FedraSansStd-medium';
          }
          .isha-answer {
            font-family: 'FedraSansStd-book-i';
            font-size: 20px;
            line-height: 1.7;
            color: #28231e;
          }
          .editor-desc {
            background: url('${config.staticPath}/assets/images/editor-note-divider.png')
              no-repeat;
            width: 100%;
            background-position: center top;
            margin-top: 40px;
            font-size: 20px;
            line-height: 1.7;
            text-align: left;
            color: #28231e;
            font-family: 'FedraSansStd-book-i';
            text-align: center;
          }
          .editor-desc .editor-inner {
            padding-top: 40px;
            text-align: left;
          }
          .sadhguruImage {
            vertical-align: middle;
            width: 101px;
            height: 101px;
            background: url('${config.imgPath}/d/46272/1663637865-bitmap.png')
              no-repeat;
            background-position: ${LangStyle[language]?.alignRTL
              ? LangStyle[language].alignRTL
              : 'left'};
            float: ${LangStyle[language]?.alignRTL
              ? LangStyle[language].alignRTL
              : 'left'};
          }
          .youtube-iframe {
            width: 100% !important;
            height: 100% !important;
            max-height: 100%;
          }
          .twitter-iframe iframe {
            margin: 10px auto !important;
            max-height: 605px;
          }
          @media only screen and (max-width: 736px) {
            .isha-article-quote {
              font-size: 20px;
            }
          }
          @media only screen and (max-width: 500px) {
            :target::before {
              content: '';
              display: block;
              height: 55px;
              margin: -55px 0 0;
            }
            .articlebody table {
              width: 100% !important;
            }
            .editor-desc .editor-inner {
              padding: 40px 3%;
              font-size: 15px;
            }
            iframe {
              width: 100%;
              min-width: 300px !important;
              margin: 0 auto;
              min-height: auto;
            }
            .articlebody iframe {
              width: 100% !important;
              height: 200px;
              min-height: auto;
            }
            iframe #twitter-widget-0 {
              height: 300px;
            }

            .articlebody .isha-article-quote-bg {
              padding: 0;
              margin: 4vw 5vw 4vw;
            }
            .isha-article-quote {
              width: 85%;
              margin-left: 0;
            }
            .poemBlock {
              padding-left: 20px;
              padding-right: 20px;
              font-size: 16px !important;
            }
            .poem-poemBlock .poem {
              font-size: 18px;
            }
            .poem-poemBlock .poem p {
              margin: 0 0 15px;
            }
            .poem-poemBlock .heading {
              margin: 4vw 0 1.4vw 0 !important;
            }
            .timeline {
              width: 50%;
            }
            .isha-article-quote {
              font-family: ${LangStyle[language]?.articleFontFamily
                ? LangStyle[language].articleFontFamily
                : 'FedraSerifAStdBook'};
              width: 84%;
              font-size: 20px;
              padding: 0 40px 0 0;
              margin: 0 0 40px 40px;
            }
            .article-right-blue {
              background-position-x: 95%;
            }
          }
          .audio-player {
            height: 80px;
            width: 100%;
            background: rgb(216 216 216);
            /* box-shadow: 0 0 20px 0 #000a; */
            font-family: arial;
            color: rgb(255 255 255);
            font-size: 0.75em;
            overflow: hidden;
            display: grid;
            grid-template-rows: 6px auto;
            opacity: 0.84;
            margin-top: -80px;
          }
          .audio-player .timeline {
            background: #979797;
            // width: 78%;
            left: 85px;
            position: relative;
            cursor: pointer;
            margin-top: 38px;
            // box-shadow: 0 2px 10px 0 #0008;
          }
          .audio-player .timeline .progress {
            background: #cf4520;
            width: 0%;
            height: 100%;
            transition: 0.25s;
          }
          .audio-player .timeline .progress:after {
            width: 20px;
            background: red;
            height: 20px;
          }
          .audio-player .controls {
            display: flex;
            justify-content: space-between;
            align-items: stretch;
            padding: 0 20px;
          }
          .audio-player .controls > * {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .audio-player .controls .toggle-play.play {
            cursor: pointer;
            position: relative;
            height: 80px;
            width: 60px;
            border: none;
            background-repeat: no-repeat;
            background-position: center;
            outline: none;
            font-family: 'FedraSansStd-medium';
            margin: 10px 0 0 0;
            background: url(${config.imgPath}/d/46272/1663639558-play.svg)
              no-repeat;
          }
          .audio-player .controls .toggle-play.play:hover {
            transform: scale(1.1);
          }
          .audio-player .controls .toggle-play.pause {
            position: relative;
            height: 80px;
            width: 60px;
            border: none;
            background-repeat: no-repeat;
            background-position: center;
            outline: none;
            font-family: 'FedraSansStd-medium';
            margin: 10px 0 0 0;
            background: url(${config.imgPath}/d/46272/1663645770-pause.svg)
              no-repeat;
          }

          .audio-player .controls .time {
            display: flex;
            margin-left: auto;
            color: #333;
            margin-top: -25px;
            font-family: 'FedraSerifAStdBook';
            font-size: 13px;
          }
          .audio-player .controls .time > * {
            padding: 2px;
          }
          .audio-player .controls .volume-container {
            cursor: pointer;
            position: relative;
            z-index: 2;
          }
          .audio-player .controls .volume-container .volume-button {
            height: 26px;
            display: flex;
            align-items: center;
          }
          .audio-player .controls .volume-container .volume-button .volume {
            transform: scale(0.7);
          }
          .audio-player .controls .volume-container .volume-slider {
            position: absolute;
            left: -3px;
            top: 15px;
            z-index: -1;
            width: 0;
            height: 15px;
            background: white;
            box-shadow: 0 0 20px #000a;
            transition: 0.25s;
          }
          .audio-player
            .controls
            .volume-container
            .volume-slider
            .volume-percentage {
            background: coral;
            height: 100%;
            width: 75%;
          }
          .audio-player .controls .volume-container:hover .volume-slider {
            left: -123px;
            width: 120px;
          }
          .toc {
            height: 260px;
            width: 160px;
            display: inline-block;
            padding: 0 0 0.1em 0;
            margin: 0 0.2em 0.2em 0;
          }
          .toc p {
            margin: 0 0 25px 0 !important;
          }
          .toc a {
            border-bottom: none;
          }
          .toc:hover {
            opacity: 0.8;
          }
          .blue {
            background: #3f687d;
          }
          .green {
            background: #56714c;
          }
          .orange {
            background: #c46f17;
          }
          .gray {
            background: #535353;
          }
          .yellow {
            background: #914444;
          }
          .purple {
            background: #6d587a;
          }
          .indexnum {
            font-family: 'FedraSansStd-A-medium';
            color: #fff;
            opacity: 0.2;
            font-size: 3em !important;
            line-height: 1em !important;
            position: absolute;
          }
          .indexcont {
            font-family: 'FedraSansStd-A-medium';
            color: #fff;
            font-size: 1.5em;
            line-height: 1em;
            padding: 3em 0 0 0em;
            text-align: center;
          }
          @media only screen and (max-width: 480px) {
            .toc {
              height: 146px;
              width: 120px;
            }
            .indexcont {
              font-size: 1em;
              line-height: 1em;
              padding: 1.5em 0 0 0em;
              text-align: center;
            }
            .indexnum {
              font-size: 3em;
              line-height: 1em;
              position: absolute;
            }
            .isha-article-downloadmsg .desktop {
              display: none;
            }
            .isha-article-downloadmsg .mobile {
              display: block;
            }
          }
          .indexnum a {
            color: #ffffff;
          }
          .indexnum {
            opacity: 0.8;
          }
          .indexcont a {
            color: #ffffff;
            font-family: 'opensans';
          }
          .indexcont a:hover {
            background-color: transparent !important;
          }
          /* aligation article */
          .aligation-faq-block {
            float: left;
            width: 100%;
            padding-left: calc((100% - 750px) / 2);
            padding-right: calc((100% - 750px) / 2);
            margin-top: 20px;
            margin-bottom: 20px;
          }
          @media (max-width: 480px) {
            .aligation-faq-block {
              padding: 0 16px;
            }
          }
          .aligation-faq-block .faq-card {
            float: left;
            margin-bottom: 20px;
            text-align: left;
            line-height: 1.3em;
          }
          .aligation-faq-block .que {
            float: left;
            width: 100%;
            background-color: #d3c9ae;
            font-family: 'FedraSansStd-Book';
            font-size: 20px;
            color: #000000;
            padding: 15px;
          }
          .aligation-faq-block .ans {
            float: left;
            width: 100%;
            background-color: #f5f5f5;
            font-family: 'FedraSansStd-Book';
            font-size: 20px;
            color: #000000;
            padding: 15px;
          }
          .aligation-faq-block .ans a {
            color: #cf4520;
            text-decoration: none;
            cursor: pointer;
          }
          .aligation-faq-block .ans p {
            margin: 0;
          }
          /* aligation article */

          /* Language styles for future reference */
          .lang-ta .articlebody .isha-article-title {
            font-family: 'MuktaMalar' !important;
            line-height: 0.73;
            font-weight: bold;
            font-size: 43px;
          }
          .lang-ta .articlebody .isha-article-message {
            font-family: 'MuktaMalar' !important;
            font-style: inherit !important;
          }
          .lang-ta .articlebody h2,
          .lang-ta .articlebody h3,
          .lang-ta .articlebody h4,
          .lang-ta .articlebody h5 {
            font-family: 'MuktaMalar' !important;
            font-style: inherit !important;
          }
          .lang-ta .articlebody .article-desc-title,
          .lang-ta .articlebody strong,
          .lang-ta .articlebody p strong {
            font-family: 'MuktaMalar' !important;
            font-weight: bold !important;
          }
          .lang-ta .articlebody .isha-article-quote {
            font-family: 'MuktaMalar' !important;
          }
          .lang-ta .articlebody .isha-article-desc {
            font-family: 'MuktaMalar' !important;
          }
          .lang-ta .isha-daily-mystic-quote-carousel .quote-cards .content {
            font-family: 'MuktaMalar' !important;
          }
          .ta.cards .isha-card-podcast-title,
          .ta.cards .isha-article-topic,
          .ta.cards .isha-rp-desc,
          .ta.quote-cards .content {
            font-family: 'MuktaMalar' !important;
          }
          .ta.cards .isha-rp-desc {
            font-size: 15px !important;
          }

          .lang-te .articlebody .isha-article-title {
            font-family: 'Gautami' !important;
            font-size: 46px;
            line-height: 0.72;
            -webkit-text-stroke: 1.4px #000000;
          }
          .lang-te .articlebody .isha-article-message {
            font-family: 'Gautami' !important;
            font-size: 22px;
            line-height: 1.55;
            font-style: inherit !important;
          }
          .lang-te .articlebody .article-desc-title {
            font-family: 'Gautami' !important;
            font-size: 28px !important;
          }
          .lang-te .articlebody .isha-article-desc {
            font-family: 'Gautami' !important;
            font-size: 22px;
            line-height: 1.64;
          }
          .lang-te .isha-daily-mystic-quote-carousel .quote-cards .content {
            font-family: 'Gautami' !important;
          }
          .te.cards .isha-card-podcast-title,
          .te.cards .isha-article-topic,
          .te.cards .isha-rp-desc,
          .te.quote-cards .content {
            font-family: 'Mukta' !important;
          }

          .lang-hi .articlebody .isha-article-title {
            font-family: 'Mukta' !important;
            line-height: 0.73;
            font-weight: bold;
            font-size: 45px !important;
          }
          .lang-hi .articlebody .isha-article-message {
            font-family: 'Mukta' !important;
            font-size: 22px;
            line-height: 1.55;
            font-style: inherit !important;
          }
          .lang-hi .articlebody .isha-article-desc {
            font-family: 'Mukta' !important;
            font-size: 22px;
            line-height: 1.73;
          }
          .lang-hi .articlebody .article-desc-title {
            font-family: 'Mukta' !important;
            font-size: 30px !important;
            font-weight: bold;
            line-height: 1.13;
            color: #28231e;
          }
          .lang-hi .articlebody .isha-article-desc {
            font-family: 'Mukta' !important;
            font-size: 22px;
            line-height: 1.73;
          }
          .lang-hi .isha-daily-mystic-quote-carousel .quote-cards .content {
            font-family: 'Mukta' !important;
          }
          .hi.cards .isha-card-podcast-title,
          .hi.cards .isha-article-topic,
          .hi.cards .isha-rp-desc,
          .hi.quote-cards .content {
            font-family: 'Mukta' !important;
          }

          .lang-ml .articlebody .isha-article-title,
          .lang-ml .articlebody .article-desc-title {
            font-family: 'NotoSansMalayalam-Bold';
            line-height: 0.77;
          }
          .lang-ml .articlebody .isha-article-message {
            font-family: 'NotoSansMalayalam-Bold' !important;
            font-size: 26px !important;
            font-style: inherit !important;
          }
          .lang-ml .articlebody .isha-article-desc {
            font-family: 'NotoSansMalayalam-Regular' !important;
            line-height: 1.8;
          }
          .lang-ml .articlebody .isha-article-quote {
            font-size: 24px;
            line-height: 1.54;
            font-family: 'NotoSansMalayalam-Regular' !important;
          }
          .lang-ml .isha-daily-mystic-quote-carousel .quote-cards .content {
            font-family: 'NotoSansMalayalam-Regular' !important;
          }
          .ml.cards .isha-card-podcast-title,
          .ml.cards .isha-article-topic,
          .ml.cards .isha-rp-desc,
          .ml.quote-cards .content {
            font-family: 'NotoSansMalayalam-Regular' !important;
          }
          .lang-ne .articlebody .isha-article-title,
          .lang-ne .articlebody .article-desc-title {
            font-family: 'NotoSans-Bold';
            line-height: 0.77;
          }
          .lang-ne .articlebody .isha-article-message {
            font-family: 'NotoSans-Bold' !important;
            font-size: 26px !important;
            font-style: inherit !important;
          }
          .lang-ne .articlebody .isha-article-desc {
            font-family: 'NotoSans-Regular' !important;
            line-height: 1.8;
          }
          .lang-ne .articlebody .isha-article-quote {
            font-size: 24px;
            line-height: 1.54;
            font-family: 'NotoSans-Regular' !important;
          }
          .lang-ne .isha-daily-mystic-quote-carousel .quote-cards .content {
            font-family: 'NotoSans-Regular' !important;
          }
          .ne.cards .isha-card-podcast-title,
          .ne.cards .isha-article-topic,
          .ne.cards .isha-rp-desc,
          .ne.quote-cards .content {
            font-family: 'NotoSans-Regular' !important;
          }

          .lang-hi .articlebody .isha-article-quote,
          .lang-te .articlebody .isha-article-quote,
          .lang-ta .articlebody .isha-article-quote {
            font-size: 20px;
            line-height: 1.42;
            font-family: 'MuktaMalar' !important;
            color: #28231e !important;
            /* font-family: "Mukta" !important;*/
          }

          .lang-ar .articlebody .isha-article-quote {
            margin: 0 50px 0 0;
            color: #6f6358;
          }
          /* Language styles for future reference */
        `}
      </style>
    </Fragment>
  );
};

export default ArticleComp;
